import React, {useEffect, useRef, useState, useMemo} from "react";
import {
    Row,
    Col,
    Card,
    Table,
    Button,
    Avatar,
    Typography,
    Space,
    Input
} from "antd";
import * as XLSX from 'xlsx';
import {getAuthenticatedUser} from "../../utils/authHelper";
import {FileExcelOutlined} from "@ant-design/icons";
import {useHistory} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {UserDataDtoGenderEnum} from "acc-rest-client";
import {loadMyModifiedLetters} from "../../store/app/app.slice";
import moment from "moment/moment";
import {
    getLanguagesLabel,
    getProcessedLetterStatusColor,
    getProcessedLetterStatusLabel,
    letterLanguageFilterDropdown,
    letterStatusFilterDropdown,
    getCustomUnicodeFlagIcon
} from "../helper/labelHelper";
import {calculate_age} from "./LetterTables";

import gender_male2_icon from "../../assets/ps-images/gender_male2_icon.png";
import gender_female_icon from "../../assets/ps-images/gender_female_icon.png";
import gender_unknown_icon from "../../assets/images/gender_unknown_icon.png";

const {Title} = Typography;

function MyModifiedLettersTable() {
    const dispatch = useDispatch();
    const componentRef = useRef();
    const [searchText, setSearchText] = useState('');
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });
    const [filters, setFilters] = useState({
        language: []
    });
    const [sorting, setSorting] = useState({
        field: 'modified',
        order: 'descend'
    });

    const {letters, isLettersLoading} = useSelector((state) => state.app);
    const history = useHistory();

    // Load data once on mount
    useEffect(() => {
        dispatch(loadMyModifiedLetters({}));
    }, [dispatch]);

    const navigateToLetterView = (record) => {
        history.push("/letters/" + record.id);
    };

    // Client-side filtering, searching, and sorting
    const filteredAndSortedData = useMemo(() => {
        let filtered = [...(letters || [])];

        // Apply search filter
        if (searchText.trim()) {
            const searchLower = searchText.toLowerCase();
            filtered = filtered.filter(letter => {
                const sender = letter?.originalLetter?.sender;
                const searchableText = [
                    letter?.id,
                    letter?.originalLetter?.letterId,
                    letter?.originalLetter?.category,
                    letter?.originalLetter?.language,
                    letter?.originalLetter?.content,
                    sender?.firstName,
                    sender?.lastName,
                    sender?.urduName,
                    sender?.email,
                    sender?.jamaat,
                    sender?.city,
                    sender?.country?.defaultLabel,
                    letter?.internalStatus,
                    letter?.filteredBy,
                    letter?.filteredByName,
                    letter?.summaryBy,
                    letter?.summaryByName,
                    letter?.answerBy,
                    letter?.answerByName
                ].filter(Boolean).join(' ').toLowerCase();
                return searchableText.includes(searchLower);
            });
        }

        // Apply language filter
        if (filters.language && filters.language.length > 0) {
            filtered = filtered.filter(letter => 
                filters.language.includes(letter?.originalLetter?.language)
            );
        }


        // Apply sorting
        if (sorting.field && sorting.order) {
            filtered.sort((a, b) => {
                let aValue, bValue;
                
                if (sorting.field === 'modified') {
                    aValue = a?.modified ? moment(a.modified).valueOf() : 0;
                    bValue = b?.modified ? moment(b.modified).valueOf() : 0;
                } else if (sorting.field === 'originalLetter.metaData.created') {
                    aValue = a?.originalLetter?.metaData?.created ? moment(a.originalLetter.metaData.created).valueOf() : 0;
                    bValue = b?.originalLetter?.metaData?.created ? moment(b.originalLetter.metaData.created).valueOf() : 0;
                } else if (sorting.field === 'originalLetter.sender.firstName') {
                    aValue = (a?.originalLetter?.sender?.firstName || '') + ' ' + (a?.originalLetter?.sender?.lastName || '');
                    bValue = (b?.originalLetter?.sender?.firstName || '') + ' ' + (b?.originalLetter?.sender?.lastName || '');
                    return sorting.order === 'ascend' 
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                } else if (sorting.field === 'originalLetter.sender.country.defaultLabel') {
                    aValue = a?.originalLetter?.sender?.country?.defaultLabel || '';
                    bValue = b?.originalLetter?.sender?.country?.defaultLabel || '';
                    return sorting.order === 'ascend' 
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                } else if (sorting.field === 'originalLetter.language') {
                    aValue = a?.originalLetter?.language || '';
                    bValue = b?.originalLetter?.language || '';
                    return sorting.order === 'ascend' 
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                } else if (sorting.field === 'internalStatus') {
                    aValue = a?.internalStatus || '';
                    bValue = b?.internalStatus || '';
                    return sorting.order === 'ascend' 
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                } else {
                    return 0;
                }

                if (sorting.field.includes('modified') || sorting.field.includes('created')) {
                    return sorting.order === 'ascend' ? aValue - bValue : bValue - aValue;
                }
                
                return 0;
            });
        }

        return filtered;
    }, [letters, searchText, filters, sorting]);

    // Paginate the filtered data
    const paginatedData = useMemo(() => {
        const start = (pagination.current - 1) * pagination.pageSize;
        const end = start + pagination.pageSize;
        return filteredAndSortedData.slice(start, end);
    }, [filteredAndSortedData, pagination]);


    const columns = [
        {
            title: "Name (Age)",
            dataIndex: "originalLetter",
            key: "originalLetter",
            sorter: true,
            sortDirections: ['ascend', 'descend'],
            render: (letter, record) => (letter ?
                <a type="link" onClick={() => navigateToLetterView(record)}>
                    <Avatar.Group>
                        <div style={{marginRight: "3px"}}>
                            <Avatar
                                src={letter.sender.gender ? letter.sender.gender === UserDataDtoGenderEnum.Male ? gender_male2_icon : letter.sender.gender === UserDataDtoGenderEnum.Female ? gender_female_icon : gender_unknown_icon : gender_unknown_icon}
                                shape="square" size="large">{letter.sender.gender}</Avatar>
                        </div>
                        <div className="avatar-info">
                            <Title level={5}>{
                                letter.sender.firstName + " " +
                                letter.sender.lastName +
                                " (" +
                                    (letter.sender.birthdate ? calculate_age(letter.sender.birthdate) : "0")
                            + ")"}
                            </Title>
                            <p>{letter.sender.email}</p>
                        </div>
                    </Avatar.Group>
                </a> : "N/A"),
        },
        {
            title: "Jamā'at, Country",
            dataIndex: ["originalLetter", "sender"],
            key: "country",
            sorter: true,
            sortDirections: ['ascend', 'descend'],
            render: (sender) => (<span>
                {sender.jamaat + ", "}
                {sender.country ? sender.country.defaultLabel : ""}&nbsp;
                {getCustomUnicodeFlagIcon(sender.country?.values?.flag)}
            </span>),
        },
        {
            title: "Language",
            dataIndex: ["originalLetter", "language"],
            key: "language",
            filters: letterLanguageFilterDropdown,
            filteredValue: filters.language,
            sorter: true,
            sortDirections: ['ascend', 'descend'],
            render: (language) => (getLanguagesLabel(language)),
        },
        {
            title: "Status",
            key: "status",
            dataIndex: ["internalStatus"],
            filters: letterStatusFilterDropdown,
            sorter: true,
            sortDirections: ['ascend', 'descend'],
            render: (status, record) => (
                <Button type="text" style={{backgroundColor: getProcessedLetterStatusColor(record), color: 'white'}}>
                    {getProcessedLetterStatusLabel(record)}
                </Button>
            ),
        },
        {
            title: "Date",
            dataIndex: ["originalLetter", "metaData", "created"],
            key: "date",
            sorter: true,
            sortDirections: ['ascend', 'descend'],
            render: (date) => (date ? moment(date).format("YYYY-MM-DD") : "N/A"),
        },
        {
            title: "Last Modification Date",
            dataIndex: ["modified"],
            key: "lastModified",
            defaultSortOrder: "descend",
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            render: (date) => {
                return date ? moment(date).format("YYYY-MM-DD HH:mm:ss") : "N/A";
            },
        },
    ];

    const handleTableChange = (paginationState, tableFilters, sorterState) => {
        // Handle filters
        const newFilters = {
            language: tableFilters["originalLetter.language"] || []
        };
        if (JSON.stringify(newFilters.language) !== JSON.stringify(filters.language)) {
            setFilters(newFilters);
        }

        // Handle sorting
        if (sorterState) {
            if (sorterState.field && sorterState.order) {
                let sortField = sorterState.field;
                if (Array.isArray(sortField)) {
                    sortField = sortField.join('.');
                }
                
                // Map column dataIndex/key to field paths
                if (sortField === 'originalLetter' || sortField === 'originalLetter.sender.firstName') {
                    sortField = 'originalLetter.sender.firstName';
                } else if (sortField === 'originalLetter.sender' || sortField === 'originalLetter.sender.country.defaultLabel' || sortField === 'country') {
                    sortField = 'originalLetter.sender.country.defaultLabel';
                } else if (sortField === 'originalLetter.language' || sortField === 'language') {
                    sortField = 'originalLetter.language';
                } else if (sortField === 'internalStatus' || sortField === 'status') {
                    sortField = 'internalStatus';
                } else if (sortField === 'originalLetter.metaData.created' || sortField === 'date') {
                    sortField = 'originalLetter.metaData.created';
                } else if (sortField === 'modified' || sortField === 'lastModified') {
                    sortField = 'modified';
                }

                setSorting({
                    field: sortField,
                    order: sorterState.order
                });
            } else {
                // Reset to default sorting when sort is cleared
                setSorting({
                    field: 'modified',
                    order: 'descend'
                });
            }
        }
    };

    const handleExportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(
            filteredAndSortedData.map(letter => ({
                'ID': letter.id,
                'Name': letter?.originalLetter?.sender ? `${letter.originalLetter.sender.firstName} ${letter.originalLetter.sender.lastName}` : '',
                'Email': letter?.originalLetter?.sender?.email || '',
                'Jamaat': letter?.originalLetter?.sender?.jamaat || '',
                'Country': letter?.originalLetter?.sender?.country?.defaultLabel || '',
                'Language': getLanguagesLabel(letter?.originalLetter?.language),
                'Status': getProcessedLetterStatusLabel(letter),
                'Date': letter?.originalLetter?.metaData?.created ? moment(letter.originalLetter.metaData.created).format("YYYY-MM-DD") : '',
                'Last Modification Date': letter?.modified ? moment(letter.modified).format("YYYY-MM-DD HH:mm:ss") : ''
            }))
        );
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "My Modified Letters");
        XLSX.writeFile(workbook, "MyModifiedLetters.xlsx");
    };

    return (
        <>
            <div className="tabled">
                <Row gutter={[24, 0]}>
                    <Col xs="24" xl={24}>
                        <Card
                            bordered={false}
                            className="criclebox tablespace mb-24"
                            title={`My Modified Letters (${filteredAndSortedData.length})`}
                            extra={<Space>
                                <Input
                                    placeholder="Search..."
                                    value={searchText}
                                    onChange={e => {
                                        const newSearchText = e.target.value;
                                        setSearchText(newSearchText);
                                        setPagination({...pagination, current: 1});
                                    }}
                                    style={{ width: 200 }}
                                />

                                {getAuthenticatedUser()?.isPortalManager() && (
                                    <Button className={"ant-tag-green"} onClick={handleExportToExcel}
                                           type="default"><FileExcelOutlined/> Export as Excel
                                    </Button>
                                )}
                            </Space>}
                        >
                            <div className="table-responsive" ref={componentRef}>
                                <Table
                                    columns={columns.map(col => {
                                        // Add sortedInfo to columns to show current sort state
                                        if (col.key === 'lastModified' && sorting.field === 'modified') {
                                            return {
                                                ...col,
                                                sortOrder: sorting.order
                                            };
                                        } else if (col.key === 'date' && sorting.field === 'originalLetter.metaData.created') {
                                            return {
                                                ...col,
                                                sortOrder: sorting.order
                                            };
                                        } else if (col.key === 'originalLetter' && sorting.field === 'originalLetter.sender.firstName') {
                                            return {
                                                ...col,
                                                sortOrder: sorting.order
                                            };
                                        } else if (col.key === 'country' && sorting.field === 'originalLetter.sender.country.defaultLabel') {
                                            return {
                                                ...col,
                                                sortOrder: sorting.order
                                            };
                                        } else if (col.key === 'language' && sorting.field === 'originalLetter.language') {
                                            return {
                                                ...col,
                                                sortOrder: sorting.order
                                            };
                                        } else if (col.key === 'status' && sorting.field === 'internalStatus') {
                                            return {
                                                ...col,
                                                sortOrder: sorting.order
                                            };
                                        }
                                        return col;
                                    })}
                                    dataSource={paginatedData}
                                    loading={isLettersLoading}
                                    pagination={{
                                        current: pagination.current,
                                        pageSize: pagination.pageSize,
                                        total: filteredAndSortedData.length,
                                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                                        showSizeChanger: true,
                                        pageSizeOptions: ['10', '20', '50', '100'],
                                        onChange: (page, pageSize) => {
                                            setPagination({ current: page, pageSize });
                                        },
                                        onShowSizeChange: (current, size) => {
                                            setPagination({ current: 1, pageSize: size });
                                        }
                                    }}
                                    onChange={handleTableChange}
                                    className="ant-border-space"
                                    rowKey="id"
                                    onRow={(record, rowIndex) => {
                                        return {
                                            onClick: (event) => {
                                                navigateToLetterView(record);
                                            },
                                            onDoubleClick: (event) => {},
                                            onContextMenu: (event) => {},
                                            onMouseEnter: (event) => {},
                                            onMouseLeave: (event) => {},
                                        };
                                    }}
                                />
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
        </>
    );
}

export default MyModifiedLettersTable;

